import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { categoryOf } from '../core/file-detect';
import type { FileCategory, JobRecord } from '../core/types';

/**
 * 本地转换记录存储。
 *
 * 只存元数据与可选缩略图（≤200KB），绝不保存原文件与转换产物：
 * 既满足隐私要求，也避免 IndexedDB 被大文件撑爆。
 */

export const HISTORY_DB_NAME = 'yogo-convert-history';
export const HISTORY_DB_VERSION = 1;
export const HISTORY_STORE = 'jobs';
/** 单条记录缩略图上限。 */
export const THUMBNAIL_MAX_BYTES = 200 * 1024;
/** 记录条数上限，超出后按时间从旧到新清理。 */
export const DEFAULT_MAX_RECORDS = 200;

export interface HistorySchema extends DBSchema {
  jobs: {
    key: string;
    value: JobRecord;
    indexes: {
      by_created_at: number;
      by_source_hash: string;
    };
  };
}

export type HistoryDatabase = IDBPDatabase<HistorySchema>;

export function openHistoryDb(name: string = HISTORY_DB_NAME): Promise<HistoryDatabase> {
  return openDB<HistorySchema>(name, HISTORY_DB_VERSION, {
    upgrade(db) {
      const store = db.createObjectStore(HISTORY_STORE, { keyPath: 'id' });
      store.createIndex('by_created_at', 'createdAt');
      store.createIndex('by_source_hash', 'sourceHash');
    },
  });
}

export interface JobQuery {
  limit?: number;
  offset?: number;
  status?: JobRecord['status'];
  category?: FileCategory | 'all';
  keyword?: string;
  sourceHash?: string;
}

export function jobCategory(record: JobRecord): FileCategory {
  return categoryOf(record.targetFormat);
}

export function matchesQuery(record: JobRecord, query: JobQuery): boolean {
  if (query.status && record.status !== query.status) return false;
  if (query.category && query.category !== 'all' && jobCategory(record) !== query.category) return false;
  if (query.sourceHash && record.sourceHash !== query.sourceHash) return false;
  if (query.keyword) {
    const keyword = query.keyword.trim().toLowerCase();
    if (keyword) {
      const haystack = `${record.sourceName} ${record.sourceFormat} ${record.targetFormat} ${record.fidelitySummary}`.toLowerCase();
      if (!haystack.includes(keyword)) return false;
    }
  }
  return true;
}

/** 按创建时间倒序游标扫描 + 内存分页，避免 N+1 查询。 */
export async function listJobs(db: HistoryDatabase, query: JobQuery = {}): Promise<JobRecord[]> {
  const limit = query.limit ?? 50;
  const offset = Math.max(0, query.offset ?? 0);
  const out: JobRecord[] = [];
  let skipped = 0;

  const transaction = db.transaction(HISTORY_STORE, 'readonly');
  let cursor = await transaction.store.index('by_created_at').openCursor(null, 'prev');
  while (cursor) {
    const record = cursor.value as JobRecord;
    if (matchesQuery(record, query)) {
      if (skipped < offset) skipped += 1;
      else {
        out.push(record);
        if (out.length >= limit) break;
      }
    }
    cursor = await cursor.continue();
  }
  await transaction.done;
  return out;
}

export async function countJobs(db: HistoryDatabase, query: JobQuery = {}): Promise<number> {
  const transaction = db.transaction(HISTORY_STORE, 'readonly');
  let cursor = await transaction.store.index('by_created_at').openCursor();
  let count = 0;
  while (cursor) {
    if (matchesQuery(cursor.value as JobRecord, query)) count += 1;
    cursor = await cursor.continue();
  }
  await transaction.done;
  return count;
}

export async function putJob(db: HistoryDatabase, record: JobRecord): Promise<void> {
  await db.put(HISTORY_STORE, record);
}

export async function getJob(db: HistoryDatabase, id: string): Promise<JobRecord | undefined> {
  return db.get(HISTORY_STORE, id);
}

export async function findLatestByHash(
  db: HistoryDatabase,
  sourceHash: string,
  targetFormat: string,
): Promise<JobRecord | undefined> {
  const records = await db.getAllFromIndex(HISTORY_STORE, 'by_source_hash', sourceHash);
  return records
    .filter((record) => record.targetFormat === targetFormat)
    .sort((a, b) => b.createdAt - a.createdAt)[0];
}

export async function deleteJob(db: HistoryDatabase, id: string): Promise<void> {
  await db.delete(HISTORY_STORE, id);
}

export async function clearJobs(db: HistoryDatabase): Promise<void> {
  await db.clear(HISTORY_STORE);
}

/** 只保留最新 maxRecords 条，返回被清理的条数。 */
export async function pruneJobs(db: HistoryDatabase, maxRecords: number = DEFAULT_MAX_RECORDS): Promise<number> {
  const total = await db.count(HISTORY_STORE);
  if (total <= maxRecords) return 0;

  const transaction = db.transaction(HISTORY_STORE, 'readwrite');
  let cursor = await transaction.store.index('by_created_at').openCursor();
  let deleted = 0;
  while (cursor && deleted < total - maxRecords) {
    await cursor.delete();
    deleted += 1;
    cursor = await cursor.continue();
  }
  await transaction.done;
  return deleted;
}
